import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideLoader2 } from '@ng-icons/lucide';

import { AuthService } from '@core/auth/auth.service';

@Component({
  selector: 'app-auth-callback',
  standalone: true,
  imports: [NgIcon],
  viewProviders: [provideIcons({ lucideLoader2 })],
  template: `
    <div class="flex min-h-screen items-center justify-center bg-background">
      @if (error(); as errorMsg) {
        <!-- Error state: ARIA role="alert" announced immediately by screen readers -->
        <div
          role="alert"
          class="w-full max-w-sm rounded-lg border border-destructive bg-card p-8 flex flex-col gap-4 opacity-0 animate-[fadeIn_150ms_ease-out_forwards]"
        >
          <h2 class="text-xl font-semibold text-destructive">Sign-in failed</h2>
          <p class="text-sm text-muted-foreground">
            We couldn't complete your sign-in. The identity provider returned an error.
            Please try again or contact support if the issue persists.
          </p>
          <button
            class="h-11 px-4 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 focus:outline-ring/50"
            type="button"
            (click)="onTryAgain()"
          >
            Try again
          </button>
        </div>
      } @else {
        <!-- Loading state: role="status" polled by screen readers -->
        <div
          role="status"
          aria-label="Completing sign-in"
          class="w-full max-w-sm rounded-lg bg-card p-8 flex flex-col items-center gap-4 opacity-0 animate-[fadeIn_150ms_ease-out_forwards]"
        >
          <ng-icon
            name="lucideLoader2"
            class="animate-spin w-6 h-6 text-primary"
          />
          <p class="text-base text-foreground">Completing sign-in&hellip;</p>
          <p class="text-sm text-muted-foreground">You will be redirected automatically.</p>
        </div>
      }
    </div>
  `,
  styles: [`
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @media (prefers-reduced-motion: reduce) {
      .animate-spin { animation: none; }
      .animate-\\[fadeIn_150ms_ease-out_forwards\\] { animation: none; opacity: 1; }
    }
  `],
})
export class AuthCallbackComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  protected readonly error = signal<string | null>(null);

  async ngOnInit(): Promise<void> {
    try {
      await this.authService.handleCallback();
      if (this.authService.isAuthenticated()) {
        await this.router.navigate(['/customers']);
      } else {
        this.error.set('Sign-in failed');
      }
    } catch {
      this.error.set('Sign-in failed');
    }
  }

  protected onTryAgain(): void {
    void this.router.navigate(['/login']);
  }
}
